import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiErrorMessage } from "@/lib/api";
import { MediaUploadField } from "./MediaUploadField";

const inputClass = "rounded-none bg-void border-line text-cream mt-1.5 focus-visible:ring-gold focus-visible:border-gold";
const slugify = (s) => s.toLowerCase().replace(/\s+/g, "-");

export function AdminResourceEditor({ resourceLabel, apiPath, fields, defaultValues, renderItem }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const slug = slugify(resourceLabel);

  const load = () =>
    api
      .get(apiPath)
      .then(({ data }) => setItems(data))
      .catch((err) => toast.error(apiErrorMessage(err)))
      .finally(() => setLoading(false));
  useEffect(() => { load(); }, [apiPath]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {};
    fields.forEach((f) => {
      payload[f.name] = f.type === "number" ? Number(editing[f.name] || 0) : editing[f.name] || "";
    });
    try {
      if (editing.id) {
        await api.put(`${apiPath}/${editing.id}`, payload);
        toast.success(`${resourceLabel} updated`);
      } else {
        await api.post(apiPath, payload);
        toast.success(`${resourceLabel} created`);
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Delete this ${resourceLabel.toLowerCase()}? This cannot be undone.`)) return;
    try {
      await api.delete(`${apiPath}/${id}`);
      toast.success(`${resourceLabel} deleted`);
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  return (
    <div data-testid={`admin-${slug}-editor`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-anton uppercase text-cream text-2xl">{resourceLabel}s</h2>
        <Button
          onClick={() => setEditing({ ...defaultValues })}
          className="rounded-none bg-gold text-void hover:bg-gold-hover font-bold uppercase tracking-wider gap-2"
          data-testid={`admin-add-${slug}-button`}
        >
          <Plus className="h-4 w-4" /> Add {resourceLabel}
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-7 w-7 text-gold animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-smoke text-sm">No {resourceLabel.toLowerCase()}s yet.</p>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between gap-4 bg-surface border border-line p-4"
              data-testid={`admin-${slug}-row-${item.id}`}
            >
              <div className="min-w-0 flex-1">{renderItem(item)}</div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-none border-line text-cream/80 hover:text-gold hover:border-gold"
                  onClick={() => setEditing({ ...item })}
                  data-testid={`admin-edit-${slug}-${item.id}`}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-none border-line text-cream/80 hover:text-red-400 hover:border-red-400"
                  onClick={() => handleDelete(item.id)}
                  data-testid={`admin-delete-${slug}-${item.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="bg-surface border-line text-cream rounded-none max-h-[85vh] overflow-y-auto" data-testid={`admin-${slug}-form-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-anton uppercase text-cream">
              {editing?.id ? `Edit ${resourceLabel}` : `Add ${resourceLabel}`}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <form onSubmit={handleSave} className="space-y-4">
              {fields.map((f) => (
                <div key={f.name}>
                  <label className="text-xs uppercase tracking-widest text-smoke">{f.label}</label>
                  {f.type === "textarea" ? (
                    <Textarea
                      required={f.required !== false}
                      rows={4}
                      value={editing[f.name] ?? ""}
                      onChange={(e) => setEditing({ ...editing, [f.name]: e.target.value })}
                      className={inputClass}
                      data-testid={`admin-field-${f.name}`}
                    />
                  ) : f.type === "image" || f.type === "video" ? (
                    <MediaUploadField
                      value={editing[f.name]}
                      onChange={(val) => setEditing({ ...editing, [f.name]: val })}
                      testId={`admin-field-${f.name}`}
                      kind={f.type}
                    />
                  ) : (
                    <Input
                      required={f.required !== false}
                      type={f.type === "number" ? "number" : "text"}
                      value={editing[f.name] ?? ""}
                      onChange={(e) => setEditing({ ...editing, [f.name]: e.target.value })}
                      className={inputClass}
                      data-testid={`admin-field-${f.name}`}
                    />
                  )}
                </div>
              ))}
              <Button
                type="submit"
                disabled={saving}
                className="rounded-none bg-gold text-void hover:bg-gold-hover font-bold uppercase tracking-wider w-full h-11"
                data-testid="admin-save-button"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
